/* src/components/Feed.js - v7.1 - Guest Safe Check Added */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Post from './Post';
import VALID_LOCATIONS from '../locations.json'; // IMPORTED FROM JSON

const AI_TONES = [
    { label: '✨ Standard', value: 'standard' },
    { label: '👔 Professional', value: 'professional' },
    { label: '🏴‍☠️ Pirate', value: 'pirate' },
    { label: '🤩 Enthusiastic', value: 'enthusiastic' },
    { label: '😏 Sarcastic', value: 'sarcastic' }
];

const COMMON_EMOJIS = [
    '😀','😂','😍','😎','🤔','😭','😡','👍','👎','🔥',
    '❤️','💔','✨','🎉','🚀','💀','🤡','💩','👻','👽',
    '🤖','🐱','🐶','🍕','🍺','☕','💻','📱','🔋','💡'
];

const FALLBACK_GIFS = [
    { id: 'f1', images: { fixed_height: { url: "https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif" }, original: { url: "https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif" } } },
    { id: 'f2', images: { fixed_height: { url: "https://media.giphy.com/media/3o7TKr3nzbh5WgCFxe/giphy.gif" }, original: { url: "https://media.giphy.com/media/3o7TKr3nzbh5WgCFxe/giphy.gif" } } },
    { id: 'f3', images: { fixed_height: { url: "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif" }, original: { url: "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif" } } }
];

function Feed({ 
    user, posts, onAddPost, onDeletePost, onLikePost, 
    onAddComment, onDeleteComment, onEditComment, onEdit, onNavigateProfile,
    onTriggerLogin, 
    showInput = true 
}) {
  
  const [inputText, setInputText] = useState('');
  const [originalText, setOriginalText] = useState(''); 
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null); 
  
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isUploading, setIsUploading] = useState(false); 
  const [activeMenu, setActiveMenu] = useState(null); 
  const [showPostModal, setShowPostModal] = useState(false);
  const [giphyKey, setGiphyKey] = useState(null);

  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState([]);
  const [isGifLoading, setIsGifLoading] = useState(false);

  const [feeling, setFeeling] = useState(''); 
  const [location, setLocation] = useState(''); 
  const [postVisibility, setPostVisibility] = useState('public'); 
  
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const feelingsList = ['Happy 😊', 'Excited 🤩', 'Tired 😴', 'Coding 💻', 'Blessed 😇', 'Annoyed 😤'];

  useEffect(() => {
      fetch('/api/config/public')
        .then(res => res.json())
        .then(data => {
            if(data.giphy_api_key) setGiphyKey(data.giphy_api_key);
        })
        .catch(err => console.error("Config fetch error", err));
  }, []);

  useEffect(() => {
      const handleEsc = (e) => {
          if (e.key === 'Escape') {
              if (activeMenu) setActiveMenu(null);
              else if (showPostModal) setShowPostModal(false);
          }
      };
      if (showPostModal) window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
  }, [showPostModal, activeMenu]);

  const toggleMenu = (menuName) => {
      setActiveMenu(activeMenu === menuName ? null : menuName);
  };

  const closeMenus = () => setActiveMenu(null);

  const searchGifs = useCallback(async (term) => {
      setIsGifLoading(true);
      try {
          if (!giphyKey) {
              setGifs(FALLBACK_GIFS);
              setIsGifLoading(false);
              return;
          }
          const query = term || "trending";
          const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${giphyKey}&q=${query}&limit=20&rating=g`);
          const data = await res.json();
          if (data.meta && data.meta.status === 200 && data.data.length > 0) setGifs(data.data);
          else setGifs(FALLBACK_GIFS);
      } catch (e) { setGifs(FALLBACK_GIFS); }
      setIsGifLoading(false);
  }, [giphyKey]);

  const handleGifSelect = (gifUrl) => {
      setPreviewUrl(gifUrl);
      setFileType('image');
      setActiveMenu(null); 
  };

  useEffect(() => {
      if (activeMenu === 'gif') searchGifs("trending");
  }, [activeMenu, searchGifs]);

  const handleMagicRewrite = async (selectedTone) => {
      if (!originalText.trim()) return; 
      setIsAiThinking(true);
      setActiveMenu(null);
      try {
          const res = await fetch('/api/ai/rewrite', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: originalText, style: selectedTone.value })
          });
          const data = await res.json();
          if (data.text) setInputText(data.text);
      } catch (e) { console.error("AI Rewrite failed", e); }
      setIsAiThinking(false);
  };

  const handleInput = (e) => {
      const val = e.target.value;
      setInputText(val);
      setOriginalText(val); 
  };

  const addEmoji = (emoji) => {
      setInputText(prev => prev + emoji);
      setOriginalText(prev => prev + emoji);
  };

  const submitPost = () => {
    if (!user) { onTriggerLogin(); return; } // GUEST GUARD
    if (inputText.trim() !== '' || previewUrl) {
      const defaultTheme = "rgba(43, 43, 54, 0.60)";
      const postColor = (user.theme_color && user.theme_color !== defaultTheme) ? user.theme_color : user.avatarColor;

      onAddPost({
        username: user.name, 
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        message: inputText, image: previewUrl, fileType: fileType, 
        feeling: feeling, location: location, color: postColor, userImage: user.profile_pic,
        visibility: postVisibility
      });

      setInputText(''); setOriginalText(''); setPreviewUrl(null); setFileType(null);
      setFeeling(''); setLocation('');
      setPostVisibility('public'); 
      setShowPostModal(false); 
      setActiveMenu(null);
    }
  };

  const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submitPost();
      }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null; 
    setFileType(file.type === 'application/pdf' ? 'pdf' : 'image');
    const formData = new FormData(); 
    formData.append('file', file);
    setIsUploading(true);
    try {
        const res = await fetch('/api/upload', { method: 'POST', body: formData });
        const data = await res.json(); 
        if (res.ok) setPreviewUrl(data.url); 
        else alert("Upload failed. Please try a smaller image.");
    } catch (err) { console.error("Upload error:", err); } finally { setIsUploading(false); }
  };

  const triggerFileUpload = () => { 
      if (fileInputRef.current) fileInputRef.current.click(); 
  };

  const handleShare = async (post, type) => {
      if (type === 'link') {
          const link = `${window.location.origin}/post/${post.id}`;
          try { await navigator.clipboard.writeText(link); alert("Link copied!"); } catch (err) {}
      } else if (type === 'repost') {
          if (!user) { onTriggerLogin(); return; }
          onAddPost({
            username: user.name, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            message: `🔄 Reposted from @${post.username}: "${post.message}"`,
            image: post.image, color: user.avatarColor, userImage: user.profile_pic,
            visibility: 'public'
          });
      }
  };

// --- FIXED GLOW (Increased Opacity & Direct Check) ---
  const getGlowStyle = (sentiment) => {
      if (!sentiment) return {};
      if (sentiment === 'POSITIVE') return { boxShadow: '0 0 25px rgba(46, 213, 115, 0.6)', border: '1px solid rgba(46, 213, 115, 0.6)' };
      if (sentiment === 'NEGATIVE') return { boxShadow: '0 0 25px rgba(255, 71, 87, 0.6)', border: '1px solid rgba(255, 71, 87, 0.6)' };
      return {};
  };

  // --- FIXED BADGE (Direct Check) ---
  const getSentimentBadge = (sentiment) => {
      if (!sentiment) return null;
      if (sentiment === 'POSITIVE') return <div style={{ fontSize: '10px', color: '#2ed573', padding: '5px 15px', fontWeight: 'bold', letterSpacing: '1px', textTransform:'uppercase', background: 'rgba(46, 213, 115, 0.15)' }}>😊 Positive Vibes</div>;
      if (sentiment === 'NEGATIVE') return <div style={{ fontSize: '10px', color: '#ff4757', padding: '5px 15px', fontWeight: 'bold', letterSpacing: '1px', textTransform:'uppercase', background: 'rgba(255, 71, 87, 0.15)' }}>⚠️ Tense</div>;
      return null;
  };

  const renderEditor = () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', position: 'relative' }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
            {user.profile_pic ? <img src={user.profile_pic} style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover' }} alt="Avatar" /> : <div style={{ background: user.avatarColor, width: '45px', height: '45px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>{user.name ? user.name[0].toUpperCase() : '?'}</div>}
            
            <div style={{ position: 'relative', flex: 1 }}>
                <textarea 
                    ref={textareaRef} 
                    className="post-input" 
                    style={{ width: '100%', padding: '18px 50px 18px 20px', borderRadius: '20px', resize: 'none', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', minHeight: '100px', fontSize: '16px', color: 'white' }} 
                    placeholder={`What's on your mind?`} 
                    value={inputText} 
                    onChange={handleInput} 
                    onKeyDown={handleKeyDown}
                    onClick={closeMenus}
                    autoFocus
                />
                <div style={{ position: 'absolute', right: '10px', bottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span onClick={(e) => { e.stopPropagation(); toggleMenu('ai'); }} style={{ cursor: 'pointer', fontSize: '18px' }} title="AI Rewrite">{isAiThinking ? '⏳' : '✨'}</span>
                </div>
            </div>
          </div>

          {(previewUrl || isUploading) && (
              <div style={{ marginTop: '5px', position:'relative', minHeight:'50px', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.2)', borderRadius:'12px' }}>
                  {isUploading ? <span style={{ color: '#2ed573' }}>⏳ Uploading...</span> : (
                      <>
                        <img src={previewUrl} style={{ maxWidth: '100%', borderRadius: '12px', maxHeight:'300px' }} alt="Preview" />
                        <button onClick={() => setPreviewUrl(null)} style={{ position:'absolute', top:'10px', right:'10px', background:'rgba(0,0,0,0.7)', color:'white', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer' }}>✕</button>
                      </>
                  )}
              </div>
          )}

          <div style={{ display: 'flex', gap: '15px', fontSize: '14px', flexWrap:'wrap', alignItems:'center', position: 'relative' }}>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*,application/pdf" onChange={handleFileSelect} />
                
                <span onClick={triggerFileUpload} style={{ color: '#2ed573', cursor: 'pointer', display:'flex', alignItems:'center', gap:'5px', padding: '8px 12px', background: 'rgba(46, 213, 115, 0.1)', borderRadius: '12px' }}>
                    {isUploading ? '⏳ Uploading...' : '📁 Photo'}
                </span>
                
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('gif'); }} style={{ color: '#a55eea', cursor: 'pointer', display:'flex', alignItems:'center', gap:'5px', padding: '8px 12px', background: 'rgba(165, 94, 234, 0.1)', borderRadius: '12px' }}>👾 GIF</span>
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('emoji'); }} style={{ color: '#ffa502', cursor: 'pointer', padding: '8px 12px', background: 'rgba(255, 165, 2, 0.1)', borderRadius: '12px' }}>🙂 Emoji</span>
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('location'); }} style={{ color: '#ff4757', cursor: 'pointer', padding: '8px 12px', background: 'rgba(255, 71, 87, 0.1)', borderRadius: '12px' }}>📍 Loc</span>
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('feeling'); }} style={{ color: '#1e90ff', cursor: 'pointer', padding: '8px 12px', background: 'rgba(30, 144, 255, 0.1)', borderRadius: '12px' }}>🎭 Feeling</span>
                
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                    <select value={postVisibility} onChange={(e) => setPostVisibility(e.target.value)} style={{ background: '#333', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '8px', borderRadius: '12px', cursor: 'pointer', outline: 'none' }}>
                        <option value="public" style={{background: '#333', color: 'white'}}>🌍 Public</option>
                        <option value="friends" style={{background: '#333', color: 'white'}}>👥 Friends</option>
                        <option value="private" style={{background: '#333', color: 'white'}}>🔒 Only Me</option>
                    </select>

                    <button onClick={submitPost} style={{ background: '#2ed573', border: 'none', padding: '10px 30px', borderRadius: '20px', color: 'white', fontWeight: 'bold', cursor: 'pointer' }} disabled={isUploading}>
                        {isUploading ? 'Wait' : 'POST'}
                    </button>
                </div>
          
                {activeMenu && (
                    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '100%', left: 0, marginTop: '10px', zIndex: 10001, background: 'rgba(30, 30, 36, 0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', width: '300px', maxWidth: '80vw' }}>
                        {activeMenu === 'emoji' && (<div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '5px', maxHeight: '200px', overflowY: 'auto' }}>{COMMON_EMOJIS.map(e => <div key={e} onClick={() => addEmoji(e)} style={{ fontSize: '24px', cursor: 'pointer', textAlign: 'center', padding: '5px' }}>{e}</div>)}</div>)}
                        {activeMenu === 'location' && (<div><input style={{ width: '100%', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '8px', borderRadius: '6px', marginBottom: '5px' }} placeholder="Search..." value={location} onChange={(e) => setLocation(e.target.value)} autoFocus />{VALID_LOCATIONS.filter(l => l.name.toLowerCase().includes(location.toLowerCase())).map(l => (<div key={l.name} onClick={() => { setLocation(l.name); setActiveMenu(null); }} style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{l.name}</div>))}</div>)}
                        {activeMenu === 'feeling' && (<div>{feelingsList.map(f => <div key={f} onClick={() => { setFeeling(f); setActiveMenu(null); }} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{f}</div>)}</div>)}
                        {activeMenu === 'ai' && (<div><div style={{ fontSize: '12px', color: '#aaa', marginBottom: '5px' }}>REWRITE STYLE:</div>{AI_TONES.map(t => <div key={t.value} onClick={() => handleMagicRewrite(t)} style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{t.label}</div>)}</div>)}
                        {activeMenu === 'gif' && (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '300px' }}>
                                <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                                    <input value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search..." style={{ flex:1, padding:'8px', borderRadius:'6px', border:'none', background:'rgba(255,255,255,0.1)', color:'white' }} />
                                    <button onClick={() => searchGifs(gifSearch)} style={{ background:'#a55eea', border:'none', color:'white', borderRadius:'6px', cursor:'pointer' }}>🔍</button>
                                </div>
                                <div style={{ flex:1, overflowY:'auto', display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'5px' }}>
                                    {isGifLoading ? <div style={{ textAlign:'center', padding:'20px' }}>Loading...</div> : gifs.map(gif => (<img key={gif.id} src={gif.images.fixed_height.url} onClick={() => handleGifSelect(gif.images.original.url)} style={{ width:'100%', borderRadius:'4px', cursor:'pointer' }} alt={gif.title} />))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
          </div>
      </div>
  );

  return (
    <div className="feed-container">
      {/* TRIGGER BAR - SAFE FOR GUESTS */}
      {showInput && (
        <div className="card" onClick={() => !user ? onTriggerLogin() : setShowPostModal(true)} style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', cursor: 'pointer' }}>
            {user && user.profile_pic ? <img src={user.profile_pic} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} alt="Avatar" /> : <div style={{ background: user ? user.avatarColor : '#555', width: '40px', height: '40px', borderRadius: '50%' }}></div>}
            <div style={{ flex: 1, color: '#aaa', background: 'rgba(255,255,255,0.05)', padding: '12px 20px', borderRadius: '30px', border: '1px solid rgba(255,255,255,0.05)' }}>
                {user ? "What's on your mind?" : "Login to post..."}
            </div>
            <div style={{ fontSize: '20px', opacity: 0.5 }}>📷</div>
        </div>
      )}

      {/* MODAL - ONLY SHOW IF USER EXISTS */}
      {showPostModal && user && (
          <div onClick={() => { setShowPostModal(false); setActiveMenu(null); }} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '40px', paddingLeft: '10px', paddingRight: '10px' }}>
              <div className="card" onClick={(e) => { e.stopPropagation(); closeMenus(); }} style={{ width: '600px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'visible', background: 'rgba(23, 23, 29, 0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', padding: '25px', borderRadius: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                      <h3 style={{ margin: 0 }}>Create Post</h3>
                      <button onClick={() => setShowPostModal(false)} style={{ background: 'none', border: 'none', color: '#ff4757', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer' }}>✕</button>
                  </div>
                  {renderEditor()}
              </div>
          </div>
      )}

      {posts.map((postData) => (
        <div key={postData.id} style={{ borderRadius: '20px', marginTop: '10px', transition: 'box-shadow 0.3s ease', overflow: 'hidden', ...getGlowStyle(postData.sentiment) }}>
            {getSentimentBadge(postData.sentiment)}
            <Post 
                post={postData} 
                user={user} 
                onDelete={onDeletePost} 
                onLike={onLikePost} 
                onAddComment={onAddComment} 
                onDeleteComment={onDeleteComment} 
                onEditComment={onEditComment} 
                onEdit={onEdit} 
                onNavigateProfile={onNavigateProfile} 
                onShare={handleShare} 
                onTriggerLogin={onTriggerLogin} // PASS DOWN
            />
        </div>
      ))}
    </div>
  );
}

export default Feed;
